import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, ShieldCheck, Sparkles, Waves } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { API, fmtError, saveSession, startGoogleLogin } from "@/lib/auth";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#07090E] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60";

const PERKS = [
  {
    icon: Waves,
    text: "Gelir, gider ve cari hesapların tek panelde, anlık nakit akışıyla.",
  },
  {
    icon: ShieldCheck,
    text: "Verilerin yalnızca sana ait; her işletme kendi izole alanında çalışır.",
  },
  {
    icon: Sparkles,
    text: "Her sabah 08:30'da yaklaşan ödeme ve tahsilatların e-postanda.",
  },
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.4 35.4 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"
    />
  </svg>
);

const Giris = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(
    location.state?.mode === "register" ? "register" : "login",
  );
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState(location.state?.error || "");
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState(0);
  const [forgot, setForgot] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then(() => navigate("/panel", { replace: true }))
      .catch(() => {});
  }, [navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    if (forgot) {
      try {
        const { data } = await axios.post(`${API}/auth/forgot-password`, {
          email: form.email,
        });
        setForgotMsg(data.message);
      } catch (err) {
        setError(fmtError(err.response?.data?.detail));
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const url =
        mode === "register" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload =
        mode === "register"
          ? form
          : { email: form.email, password: form.password };
      const { data } = await axios.post(url, payload);
      saveSession(data);
      toast.success(
        mode === "register"
          ? `Hoş geldin, ${data.name}. Hesabın hazır.`
          : `Tekrar hoş geldin, ${data.name}.`,
      );
      navigate(data.role === "admin" ? "/admin" : "/panel", {
        replace: true,
        state: { user: data },
      });
    } catch (err) {
      setError(fmtError(err.response?.data?.detail));
      setErrorCode(err.response?.status || 0);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      data-testid="auth-page"
      className="flex min-h-screen flex-col bg-[#07090E] text-slate-50"
    >
      <header className="glass-panel border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="auth-logo" aria-label="Akış ana sayfa">
            <Logo size={30} />
          </Link>
          <Link
            to="/"
            data-testid="auth-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-emerald-400"
          >
            <ArrowLeft className="h-4 w-4" /> Siteye dön
          </Link>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-6xl flex-1 items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.1fr_1fr]">
        <motion.section
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block"
        >
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            İşletme Hesabı
          </div>
          <h1 className="font-display mt-4 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
            Nakit akışını
            <br />
            <span className="text-emerald-400">kendi hesabında</span>
            <br />
            yönet.
          </h1>
          <ul className="mt-10 space-y-5">
            {PERKS.map((p) => (
              <li
                key={p.text}
                className="flex items-start gap-4 text-sm text-slate-400"
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-500/10">
                  <p.icon className="h-4 w-4 text-emerald-400" />
                </span>
                <span className="pt-1.5">{p.text}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mx-auto w-full max-w-md rounded-3xl border border-white/8 bg-[#0D111A] p-7 sm:p-9"
        >
          <div
            data-testid="auth-mode-tabs"
            className="grid grid-cols-2 rounded-xl border border-white/10 bg-[#07090E] p-1 text-sm font-semibold"
          >
            {[
              ["login", "Giriş Yap"],
              ["register", "Hesap Oluştur"],
            ].map(([m, label]) => (
              <button
                key={m}
                type="button"
                data-testid={`auth-tab-${m}`}
                onClick={() => {
                  setMode(m);
                  setError("");
                  setForgot(false);
                  setForgotMsg("");
                }}
                className={`rounded-lg py-2.5 transition-all duration-300 ${
                  mode === m
                    ? "bg-emerald-500 text-emerald-950"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid="auth-google-btn"
            onClick={startGoogleLogin}
            className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-xl border border-white/12 bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition-all duration-300 hover:bg-slate-100"
          >
            <GoogleIcon /> Google ile devam et
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-600">
            <span className="h-px flex-1 bg-white/10" /> veya e-posta ile{" "}
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <form data-testid="auth-form" onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <input
                  data-testid="auth-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Ad Soyad"
                  className={inputCls}
                />
                <input
                  data-testid="auth-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="İşletme adı (isteğe bağlı)"
                  className={inputCls}
                />
              </>
            )}
            {forgot && (
              <p
                data-testid="auth-forgot-hint"
                className="text-xs leading-relaxed text-slate-400"
              >
                Kayıtlı e-postanı yaz; sana 60 dakika geçerli, tek kullanımlık
                bir sıfırlama bağlantısı gönderelim.
              </p>
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              placeholder="E-posta"
              className={inputCls}
            />
            {!forgot && (
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={mode === "register" ? 8 : 1}
                value={form.password}
                onChange={set("password")}
                placeholder={
                  mode === "register" ? "Şifre (en az 8 karakter)" : "Şifre"
                }
                className={inputCls}
              />
            )}
            {forgotMsg && (
              <div
                data-testid="auth-forgot-success"
                className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2.5 text-xs text-emerald-200"
              >
                {forgotMsg}
              </div>
            )}
            {error && (
              <div
                data-testid="auth-error"
                className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300"
              >
                {error}
                {mode === "login" && errorCode === 404 && (
                  <button
                    type="button"
                    data-testid="auth-error-register-link"
                    onClick={() => {
                      setMode("register");
                      setError("");
                      setErrorCode(0);
                    }}
                    className="mt-2 block font-semibold text-emerald-300 underline-offset-2 hover:underline"
                  >
                    Hesap Oluştur sekmesine geç →
                  </button>
                )}
                {mode === "login" && errorCode === 401 && !forgot && (
                  <button
                    type="button"
                    data-testid="auth-error-forgot-link"
                    onClick={() => {
                      setForgot(true);
                      setError("");
                      setErrorCode(0);
                    }}
                    className="mt-2 block font-semibold text-emerald-300 underline-offset-2 hover:underline"
                  >
                    Şifremi sıfırla →
                  </button>
                )}
              </div>
            )}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_28px_rgba(0,255,135,0.3)] disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {forgot
                ? "Sıfırlama Bağlantısı Gönder"
                : mode === "register"
                  ? "Hesabımı Oluştur"
                  : "Giriş Yap"}
            </button>
            {mode === "login" && (
              <button
                type="button"
                data-testid="auth-forgot-toggle"
                onClick={() => {
                  setForgot(!forgot);
                  setError("");
                  setForgotMsg("");
                }}
                className="block w-full text-center text-xs font-medium text-slate-500 transition-colors duration-300 hover:text-emerald-400"
              >
                {forgot ? "← Girişe dön" : "Şifremi unuttum"}
              </button>
            )}
          </form>
          <p className="mt-5 text-center text-xs leading-relaxed text-slate-600">
            Devam ederek verilerinin yalnızca sana ait izole bir alanda
            saklanacağını kabul edersin. Şifreni asla e-postayla istemeyiz.
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default Giris;
